

<script>
/**
 * Component that separates any number of hidable panels
 *
 * @vue-prop {Array} [allowHiding=[...true]] - Optional. Same behaviour as above.
 *                   Each item of the array is a boolean to allow / disallow hiding a panel
 * @vue-prop {Array} [customWidthPerc=[...100 / cols]] - Optional.
 *                   Each item of the array is the percentage width of panel
 * @vue-prop {Boolean} columnMode - Switch display to column
 * @vue-prop {Array|Boolean} value - Toggle panels visibility. Undefined or true means visible.
 *                                   If Array, each value at its index represent its corresponding panel visibility.
 *
 * @vue-event input - Emit `value` (Array form), triggered when a panel is toggled.
 */
// Components

export default {
  name: 'PanelSplitter',

  props: {
    allowHiding: {
      type: Array,
      default: null,
    },
    customWidthPerc: {
      type: Array,
      default: null,
    },
    columnMode: Boolean,
    value: {
      type: [Array, Boolean],
      default: true,
    },
  },

  data() {
    return {
      panelsShown: [],
      nSlots: 1,
    };
  },

  computed: {
    classes() {
      return {
        'panel-splitter--column': !!this.columnMode,
        'panel-splitter--row': !this.columnMode,
      };
    },
    nCols() {
      return this.nSlots;
    },
    hidingAllowed() {
      const a = [];
      for (let i = 0; i < this.nCols; i++) {
        a.push(true);
      }
      if (this.allowHiding && this.allowHiding.length === this.nCols) {
        for (let i = 0; i < this.nCols; i++) {
          a[i] = !!this.allowHiding[i] === true;
        }
      }
      return a;
    },
    sectionSizePercentages() {
      if (this.customWidthPerc && this.customWidthPerc.length === this.nCols) {
        return this.customWidthPerc;
      }
      const width = 100 / this.nCols;
      const percs = [];
      for (let i = 0; i < this.nCols; i++) {
        percs.push(width);
      }
      return percs;
    },
    totalWidthInvisible() {
      return this.panelsShown.reduce((acc, cur, idx) => {
        if (!cur) {
          return acc + this.sectionSizePercentages[idx];
        }
        return acc;
      }, 0);
    },
  },

  watch: {
    value() {
      this.syncPanelsShown();
    },
  },

  created() {
    this.syncPanelsShown();
  },

  mounted() {
    this.nSlots = Number(this.$slots.default && this.$slots.default.length) || 1;
  },

  updated() {
    this.nSlots = Number(this.$slots.default && this.$slots.default.length) || 1;
  },

  methods: {
    syncPanelsShown() {
      if (!this.$slots.default) {
        this.panelsShown = [];
        return;
      }
      this.panelsShown = this.$slots.default.map((slot, index) => {
        if (this.value instanceof Array) {
          return this.value[index] === undefined || !!this.value[index];
        }
        return !!this.value;
      });
    },
    /**
     * Hide/show panels
     * @param {Number} index - Index of the panel to toggle
     */
    togglePanel(index) {
      this.$set(this.panelsShown, index, !this.panelsShown[index]);
      this.$emit('input', this.panelsShown);
    },
    panelClass(index) {
      const classes = {
        'panel-splitter__panel--hidden': !this.panelsShown[index],
      };
      classes[`panel-splitter-col-${index}`] = true;
      if (index === 1) {
        if (this.nCols === 2) {
          classes['split-right'] = true;
        } else if (this.nCols === 3) {
          classes['split-mid'] = true;
        }
      }
      return classes;
    },
    panelStyle(index) {
      if (this.columnMode || !this.panelsShown[index]) {
        return {};
      }
      return {
        'flex-basis': `${(this.sectionSizePercentages[index])}%`,
      };
    },
    onTransitionEnd(index) {
      this.$emit('transitionEnd', { index });
    },
    genHideButton(index) {
      return this.$createElement(
        'img',
        {
          staticClass: 'panel-splitter__eye',
          attrs: {
            src: '/images/see.png',
            alt: '',
          },
          on: {
            click: () => { this.togglePanel(index); },
          },
        },
      );
    },
    genPanels() {
      if (!this.$slots.default) return [];
      return this.$slots.default.map((slotVNode, index) => {
        const children = this.panelsShown[index] ? [slotVNode] : [];
        if (this.hidingAllowed[index] || !this.panelsShown[index]) {
          children.unshift(this.genHideButton(index));
        }
        return this.$createElement(
          'div',
          {
            key: index,
            staticClass: 'panel-splitter__panel',
            class: this.panelClass(index),
            style: this.panelStyle(index),
            on: {
              transitionend: () => { this.onTransitionEnd(index); },
            },
          },
          children,
        );
      });
    },
  },
  render(h) {
    return h(
      'div',
      {
        staticClass: 'panel-splitter',
        class: this.classes,
      },
      this.genPanels(),
    );
  },
};
</script>

<style lang="scss">
@import "@/assets/scss/vars.scss";


.panel-splitter {
  &__panel {
    box-sizing: border-box;
    display: inline-block;
    overflow: auto;
    position: relative;
    vertical-align: top;
    flex-grow: 1;
    transition-property: flex-basis, flex-grow;
    transition-duration: 0.15s;

    &--hidden {
      flex-basis: 40px;
      flex-grow: 0;
      overflow: hidden;
    }
  }
  &__eye {
    display: block;
    width: 18px;
    position: absolute;
    top: 4px;
    left: 2px;
    cursor: pointer;
    padding: 7px;
    z-index: 10001;
    background: #fff;
    box-shadow: 0px 0px 4px #aaa;
    border-radius: 18px;

    &:hover {
      background: $color-hoverbtn-light-blue;
      box-shadow: 0px 0px 4px #6694cc;
    }
  }
  &--row {
    display: flex;
    flex-direction: row;

    .panel-splitter {
      &__panel {
        height: 100%;
        min-width: 40px;

        &:not(:first-child) {
          border-left: 4px solid #dfdfdf;
        }
      }
      &__content {
        height: 100%;
      }
    }
  }
  &--column {
    overflow: auto;

    .panel-splitter {
      &__panel {
        display: block;
        min-height: 40px;
        overflow: intial;
        width: 100%;

        &:not(:first-child) {
          border-top: 4px solid #dfdfdf;
        }
      }
      &__content {
        width: 100%;
      }
    }
  }
}

@media (max-width: $smartphone-landscape) {
  .panel-splitter {
    white-space: nowrap;

    &__panel {
      white-space: initial;
    }
    &__eye {
      width: 15px;
      padding: 5px;
    }
  }
}
</style>
